// ─────────────────────────────────────────────
//  StatusPill — canonical room status badge (D-07, D-11)
//  Label comes from getStatusMeta(status) (single source of truth in
//  domain/status.js) — never hard-code a status label. The taxonomy
//  meta hexes are DARK-mode values, so they are NOT rendered raw;
//  instead we apply the accessible LIGHT-mode tint table below.
//  Always renders the label text (never color alone, D-11).
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import { getStatusMeta, normalizeStatus } from "../domain/status";

const { radius, font } = tokens;

// Accessible light-mode tints now live in tokens.color.status — the single
// source shared with the RoomCard status bar, so pill and card border can
// never diverge. (The raw dark meta.pill/meta.text stay in domain/status.)
const LIGHT = tokens.color.status;

/**
 * `label` — an override for the WORD only. The colour still comes from the
 * canonical status.
 *
 * ⚠️ ONE CHIP, AND IT SAYS THE PHASE. The card used to print the status pill
 * ("CLOSING") with the live phase underneath it ("Waking up"), which is the
 * board telling you two things about one room — and they genuinely differ,
 * because `phaseStatus` maps BOTH `closing` and `emergence` onto the `closing`
 * status. Owner, 2026-09-07: "use only the top… when is closing is closing,
 * then waking up, not closing do waking up".
 *
 * The phase is the more precise fact, so it becomes the word. The status keeps
 * owning the COLOUR, so a room in emergence still reads amber like the rest of
 * `closing` and the C-002 colour vocabulary is untouched.
 *
 * ⚠️ AND "READY" IS NEVER PRINTED. `to_room` writes status `ready`, so a room
 * somebody is merely walking to was announcing READY — a word that, on a board,
 * invites the next patient. Owner: "remove ready". With a phase it prints "To
 * room"; without one it prints nothing at all rather than a word that means
 * something it does not.
 */
export default function StatusPill({ status, label: labelOverride = null }) {
  const key = normalizeStatus(status);
  const { label: statusLabel } = getStatusMeta(status);
  const label = labelOverride || statusLabel;
  const tint = LIGHT[key] || LIGHT.empty;

  if (!labelOverride && key === "ready") return null;

  return (
    <span
      aria-label={"Status: " + label}
      style={{
        ...styles.pill,
        background: tint.bg,
        color: tint.text,
        border: "1px solid " + tint.border,
      }}
    >
      {label}
    </span>
  );
}

const styles = {
  pill: {
    display: "inline-flex",
    alignItems: "center",
    // Scale with the TV factor (--atc-scale; 1 everywhere else) — the pill was
    // the ONE card element that stayed 12px on a wall display.
    height: "calc(22px * var(--atc-scale, 1))",
    padding: "4px 8px",
    boxSizing: "border-box",
    fontSize: `calc(${font.size.label}px * var(--atc-scale, 1))`,
    fontWeight: font.weight.bold,
    fontFamily: font.family,
    borderRadius: radius.full,
    lineHeight: 1,
    whiteSpace: "nowrap",
    // Distance legibility (wall TV / across the room): caps + tracking make
    // the short status words readable far smaller than mixed case.
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
};
